import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';

import { Form, Typography, Divider, Card, Drawer, Modal, message } from 'antd';

import {
  ApplicationUIContext,
  ButtonGroup,
  useDrawerState,
  useModalState,
  helpers,
} from '@yuhuinc/ui-library';
import { CurrentApplicationContext } from '../ApplicationOverview';
import BaseApplicationForm from '../common/BaseApplicationForm';
import CancelApplicationForm from './overview/CancelApplicationForm';

import { submitPayload, resetApplicationStatuses } from '../store/actions';
import { showFailure } from 'actions/notificationActions';
import { getTranslatedString } from 'helpers/i18n';
import { StyledHeader } from '../common/Styles';
import {
  canCancelApplication,
  shouldNotifyChangeApplicants,
  generateBaseApplicationFormInitialValues,
} from '../common/helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiPutRequest } from 'helpers/ApiHelper';
import NotifyChangeToApplicantsModal from './base_application/NotifyChangeToApplicantsModal';

const { COMPACT_CARD_WIDTH } = helpers;

const { Title } = Typography;

const ChangesCompleteText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.ChangesComplete'
);
const SaveText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.Save'
);
const CancelText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.CancelApplication'
);

const BaseApplication = ({ disableRentForm, showFailure, resetApplicationStatuses }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const application = useContext(CurrentApplicationContext);
  const [applicationInviteForm] = Form.useForm();
  const [, cancelApplicationDrawer] = useDrawerState(false);
  const notifyApplicantsAfterSaveModal = useModalState(false);
  const queryClient = useQueryClient();
  const shouldShowChangesCompleteButton = application?.lifecycle?.has_pending_changes_to_notify;
  const canCancel = canCancelApplication(application);

  useEffect(() => {
    if (application) {
      applicationInviteForm.setFieldsValue(generateBaseApplicationFormInitialValues(application));
    }
  }, [application]);

  const updateApplicationMutation = useMutation({
    mutationFn: (fieldsValue) => {
      const payload = submitPayload(fieldsValue);
      return apiPutRequest(
        `/landlords/application_management/applications/${application.id}`,
        payload
      );
    },
    // TODO: Update message once we get feedback from Design team
    onSuccess: () => {
      const shouldShowModalToNotifyApplicants = shouldNotifyChangeApplicants(application);

      if (shouldShowModalToNotifyApplicants) {
        notifyApplicantsAfterSaveModal.open();
      } else {
        Modal.success({
          title: 'Base Application Saved',
          content: 'The base application has been saved successfully.',
          onOk: () => {
            resetApplicationStatuses();
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: () => message.error(getTranslatedString('unknownError')),
  });

  const savePendingChangesToNotifyMutation = useMutation({
    mutationFn: () => {
      return apiPost(
        `/landlords/application_management/applications/${application.id}/save_pending_changes_to_notify`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  return (
    <Card style={{ maxWidth: COMPACT_CARD_WIDTH }}>
      <StyledHeader>
        <Title level={isMobile ? 3 : 4}>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.baseApplication.cardTitle'
          )}
        </Title>

        <ButtonGroup
          buttons={[
            shouldShowChangesCompleteButton && {
              label: ChangesCompleteText,
              type: 'primary',
              onClick: notifyApplicantsAfterSaveModal.open,
              loading: notifyApplicantsAfterSaveModal.isVisible,
            },
            {
              label: SaveText,
              onClick: applicationInviteForm.submit,
              type: shouldShowChangesCompleteButton ? null : 'primary',
              loading: updateApplicationMutation.isLoading,
            },
            canCancel && {
              label: CancelText,
              onClick: cancelApplicationDrawer.open,
            },
          ].filter(Boolean)}
        />
      </StyledHeader>

      <Divider />

      <BaseApplicationForm
        form={applicationInviteForm}
        formInitialValues={generateBaseApplicationFormInitialValues(application)}
        onSubmit={updateApplicationMutation.mutate}
        onError={showFailure}
        disableRentForm={disableRentForm}
        mode="edit"
      />
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.title'
        )}
        visible={cancelApplicationDrawer.isVisible}
        closable
        destroyOnClose
        onClose={cancelApplicationDrawer.close}
      >
        <CancelApplicationForm afterSubmit={cancelApplicationDrawer.close} />
      </Drawer>
      <NotifyChangeToApplicantsModal
        visible={notifyApplicantsAfterSaveModal.isVisible}
        onClose={notifyApplicantsAfterSaveModal.close}
        onCancel={() => {
          notifyApplicantsAfterSaveModal.close();
          savePendingChangesToNotifyMutation.mutate();
        }}
        afterSubmit={notifyApplicantsAfterSaveModal.close}
      />
    </Card>
  );
};

const mapStoreToProps = (store) => ({
  // Following the old flow logic (disableLeaseWizardSelections)
  // to disable the rent form section
  disableRentForm:
    store.companyDetails.companyPreferences.disable_lease_wizard_selections &&
    !store.userTypes.admin,
});

const mapDispatchToProps = {
  showFailure,
  resetApplicationStatuses,
};

export default connect(mapStoreToProps, mapDispatchToProps)(BaseApplication);
